"use client";
import { useEffect, useRef, useState } from "react";
import SearchModal from "../search-modal";
import CloseSvg from "@/components/svg/header/close";
import { t } from "@lingui/core/macro";
import { usePathname } from "next/navigation";
import { useSearchStore } from "@/components/context/search-store";
import useLocalizedRouter from "@/hooks/use-localized-router";

interface SearchProps {
  searchModal: boolean;
  setSearchModal: (searchModal: boolean) => void;
  lang: string;
}

const SearchBar: React.FC<SearchProps> = ({ searchModal, setSearchModal, lang }) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useLocalizedRouter();
  const { setSearchTerm } = useSearchStore();

  const [localSearchTerm, setLocalSearchTerm] = useState<string>("");
  const [showResults, setShowResults] = useState<boolean>(false);

  useEffect(() => {
    if (searchModal && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current?.contains(event.target as Node) ||
        inputRef.current?.contains(event.target as Node) ||
        document.getElementById("search-modal-results")?.contains(event.target as Node)
      ) {
        return;
      }
      closeSearchModal();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(event.target.value);
    setShowResults(event.target.value.length >= 3);
  };

  const closeSearchModal = () => {
    setSearchModal(false);
    setShowResults(false);
  };

  const pathname = usePathname();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      inputRef.current?.blur();
      closeSearchModal();
      setLocalSearchTerm("");
    }
    previousPathnameRef.current = pathname;
  }, [pathname]);

  const handleSearchSubmit = () => {
    setSearchTerm(localSearchTerm);
    router.push("/search?s=" + encodeURIComponent(localSearchTerm));
    closeSearchModal();
  };

  const renderSearchInput = (isModal: boolean = false) => (
    <div className="relative w-full">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
          />
        </svg>
      </div>
      <input
        ref={inputRef}
        data-testid="search-bar"
        placeholder={t`Enter search term...`}
        type="text"
        value={localSearchTerm}
        onChange={handleInputChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleSearchSubmit();
          }
        }}
        onFocus={() => {
          if (localSearchTerm.length >= 3) {
            setShowResults(true);
          }
        }}
        className={`h-[42px] w-full rounded-[28px] pl-10 pr-4 focus:outline-none ${isModal ? "border-0 bg-[#f2f2f3]" : "border border-transparent bg-[#f2f2f3] focus:border-black focus:bg-white"} truncate`}
      />
    </div>
  );

  const renderMobileSearchInputModal = () => (
    <div className="flex items-center justify-between gap-4 bg-white p-4">
      {renderSearchInput(true)}
      <div onClick={closeSearchModal} className="cursor-pointer">
        <CloseSvg />
      </div>
    </div>
  );

  const renderSearchResults = () => {
    if (showResults) {
      return (
        <div
          id="search-modal-results"
          className="absolute z-50 mt-0 flex h-[calc(100%-74px)] w-full flex-col overflow-auto bg-white md:h-auto md:max-h-[600px] md:w-auto md:shadow-[0_10px_30px_0_rgba(0,0,0,0.25)]"
        >
          <SearchModal
            setSearchTerm={setLocalSearchTerm}
            lang={lang}
            searchTerm={localSearchTerm}
            onClose={closeSearchModal}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {searchModal && (
        <div className="fixed left-0 top-0 z-40 h-full w-full bg-black bg-opacity-20 transition-opacity duration-300">
          <div ref={modalRef}>
            {renderMobileSearchInputModal()}
            {renderSearchResults()}
          </div>
        </div>
      )}

      <div className="hidden md:block md:w-[275px] lg:w-[380px]">
        {renderSearchInput()}
        {renderSearchResults()}
      </div>
    </>
  );
};

export default SearchBar;
